// Lib
import React, { useRef, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty, get } from 'lodash/fp';
import { createPortal } from 'react-dom';

// Utils
import { getDrawingViewBox } from '../../utils/drawingCanvasUtils';
import * as pointLib from '../../../../../../common/maths/geometry/point';
import { hasCommandModifier, hasShiftKey } from '../../../../../utils/keyboard/keyboardUtility';
import { isAttemptingMultiSelect } from '../../../../selection/selectionUtils';
import { reorderPathsToBottom, reorderPathsToTop, shiftPaths } from '../../drawingEditorUtils';
import { exitDrawingEditorOnEscape } from '../../drawingEditorEventUtils';
import useDrawingPathHitTest from '../inputManager/useDrawingPathHitTest';
import useDrawingCanvasModeInputLayerDelegate from '../useDrawingCanvasModeInputLayerDelegate';

// Components
import DrawingEditorCanvasSvg from '../DrawingEditorCanvasSvg';
import DrawingSelectionMarquee from './DrawingSelectionMarquee';
import {
    DrawingEditorSelectModeSvgHitAreaGroup,
    DrawingEditorSelectModeSvgStaticGroup,
    DrawingEditorSelectModeSvgTransformGroup,
} from './DrawingEditorSelectModeSvgGroups';

// Hooks
import useDrawingEditorSelectionMarqueePathFinder from './useDrawingEditorSelectionMarqueePathFinder';
import useDrawingEditorUpdateToolbarOnSelection from './useDrawingEditorUpdateToolbarOnSelection';
import useDrawingEditorSelectionMarqueeState from './useDrawingEditorSelectionMarqueeState';
import useDrawingEditorSelectedPathModifier from './useDrawingEditorSelectedPathModifier';
import useDrawingEditorDragAndDropState from './useDrawingEditorDragAndDropState';
import useDrawingEditorSelectionState from './useDrawingEditorSelectionState';
import useDocumentKeyEventHandler from '../useDocumentKeyEventHandler';

// Constants
import { KEY_CODES } from '../../../../../utils/keyboard/keyConstants';

// Styles
import './DrawingEditorCanvasSelectMode.scss';

const handleShiftPaths = ({ event, defaultOffset, selectedPathIdsMap, paths, setPaths, canvasScale = 1 }) => {
    event.stopPropagation();
    event.preventDefault();

    if (isEmpty(selectedPathIdsMap)) return;

    const scaledDefaultOffset = pointLib.scale(1 / canvasScale, defaultOffset);
    const offset = hasShiftKey(event) ? pointLib.scale(10, scaledDefaultOffset) : scaledDefaultOffset;

    const newPaths = shiftPaths(offset, paths, selectedPathIdsMap);
    return setPaths(newPaths);
};

const handleReorderPathsToTop = ({ event, selectedPathIdsMap, paths, setPaths }) => {
    event.stopPropagation();
    event.preventDefault();

    setPaths(reorderPathsToTop(paths, selectedPathIdsMap));
};

const handleReorderPathsToBottom = ({ event, selectedPathIdsMap, paths, setPaths }) => {
    event.stopPropagation();
    event.preventDefault();

    setPaths(reorderPathsToBottom(paths, selectedPathIdsMap));
};

/**
 * Escape in select mode clears the selection when there is one, and otherwise exits the editor.
 */
export const handleSelectModeEscapeKey = ({
    event,
    selectedPathIdsMap,
    clearSelectedPaths,
    paths,
    saveAndExitDrawingEditor,
    discardAndExitDrawingEditor,
}) => {
    if (isEmpty(selectedPathIdsMap)) {
        return exitDrawingEditorOnEscape({
            event,
            saveAndExit: () => saveAndExitDrawingEditor(paths),
            discardAndExit: discardAndExitDrawingEditor,
        });
    }

    event.preventDefault();
    event.stopPropagation();
    clearSelectedPaths();
};

const DrawingEditorCanvasSelectMode = (props) => {
    const {
        paths,
        setPaths,
        removePaths,

        canvasScale = 1,

        saveAndExitDrawingEditor,
        discardAndExitDrawingEditor,
        debugUpdateAllStrokes,
        debugDrawingLastUpdateTime,
        enableDrawingInkEffect,

        setStrokeSizeState,
        setStrokeColorState,
        setToolModeState,
        onPathPropertyChangeRef,
        externalInputLayerRef,
        inputLayerDelegateRef,
    } = props;
    const pointerEventStats = useRef({
        moveCount: 0,
        pointerDownPathId: null,
    });

    const { selectedPathIdsMap, toggleSelectedPath, setSelectedPathIds, clearSelectedPaths } =
        useDrawingEditorSelectionState();

    const {
        isMarqueeSelectMode,
        isAppendMode,
        drawingMarqueeDimensions,
        viewportMarqueeDimensions,
        initialSelectedPathIdsMap,
        startMarqueeSelectMode,
        dragMarqueeSelectionTo,
        endMarqueeSelectMode,
    } = useDrawingEditorSelectionMarqueeState();

    useDrawingEditorSelectionMarqueePathFinder({
        paths,
        isMarqueeSelectMode,
        isAppendMode,
        initialSelectedPathIdsMap,
        marqueeDimensions: drawingMarqueeDimensions,
        setSelectedPathIds,
    });

    const { startDrag, onDrag, endDrag, transformGroupRef, isDragging, isDragMove, draggedPathIdsMap } =
        useDrawingEditorDragAndDropState({ paths, setPaths });

    useEffect(() => {
        if (isMarqueeSelectMode) return;

        const hasSelection = !isEmpty(selectedPathIdsMap);
        setToolModeState({ hasSelection });
    }, [selectedPathIdsMap, isMarqueeSelectMode]);

    useDrawingEditorSelectedPathModifier({
        paths,
        setPaths,
        canvasScale,
        selectedPathIdsMap,
        onPathPropertyChangeRef,
        enableDrawingInkEffect,
    });

    useDrawingEditorUpdateToolbarOnSelection({
        isMarqueeSelectMode,
        paths,
        selectedPathIdsMap,
        setStrokeSizeState,
        setStrokeColorState,
        canvasScale,
    });

    const getPathIdFromClientPoint = useDrawingPathHitTest({
        inputLayerRef: externalInputLayerRef,
        requiredClassName: 'path-hit-area',
    });

    const inputLayerDelegate = useMemo(
        () => ({
            onPointerDown: ({ event, canvasPoint, viewportPoint, clientPoint }) => {
                if (event.buttons !== 1) return;

                event.preventDefault();

                const hitPathId = getPathIdFromClientPoint(clientPoint);
                pointerEventStats.current.pointerDownPathId = hitPathId;
                pointerEventStats.current.moveCount = 0;

                if (hitPathId) {
                    const isSelected = selectedPathIdsMap && selectedPathIdsMap[hitPathId];
                    const draggedIdsMap = isSelected ? selectedPathIdsMap : { [hitPathId]: true };

                    startDrag(canvasPoint, draggedIdsMap);
                    return;
                }

                const appendMode = isAttemptingMultiSelect(event);

                if (!appendMode) clearSelectedPaths();

                startMarqueeSelectMode({
                    drawingPoint: canvasPoint,
                    viewportPoint,
                    appendMode,
                    selectedPathIdsMap,
                });
            },
            onPointerMove: ({ event, canvasPoint, viewportPoint }) => {
                if (isDragging) {
                    pointerEventStats.current.moveCount++;
                    event.preventDefault();
                    onDrag(canvasPoint);
                    return;
                }

                if (!isMarqueeSelectMode) return;

                event.preventDefault();
                dragMarqueeSelectionTo({
                    drawingPoint: canvasPoint,
                    viewportPoint,
                });
            },
            onPointerUp: ({ event, canvasPoint }) => {
                const pointerDownPathId = pointerEventStats.current.pointerDownPathId;
                pointerEventStats.current.pointerDownPathId = null;

                event.preventDefault();

                if (isMarqueeSelectMode) {
                    endMarqueeSelectMode();
                    return;
                }

                endDrag(canvasPoint);

                if (pointerEventStats.current.moveCount > 2) return;
                if (!pointerDownPathId) return;

                if (isAttemptingMultiSelect(event)) {
                    toggleSelectedPath(pointerDownPathId);
                    return;
                }

                setSelectedPathIds([pointerDownPathId]);
            },
            onPointerCancel: ({ canvasPoint }) => {
                pointerEventStats.current.pointerDownPathId = null;
                endDrag(canvasPoint);
                endMarqueeSelectMode();
            },
        }),
        [
            clearSelectedPaths,
            dragMarqueeSelectionTo,
            endDrag,
            endMarqueeSelectMode,
            getPathIdFromClientPoint,
            isDragging,
            isMarqueeSelectMode,
            onDrag,
            selectedPathIdsMap,
            setSelectedPathIds,
            startDrag,
            startMarqueeSelectMode,
            toggleSelectedPath,
        ],
    );
    useDrawingCanvasModeInputLayerDelegate({ inputLayerDelegateRef, inputLayerDelegate });

    const onKeyDown = useCallback(
        (event) => {
            switch (event.keyCode) {
                case KEY_CODES.A: {
                    if (!hasCommandModifier(event)) return;
                    const allPathIds = paths.map(get('id'));
                    setSelectedPathIds(allPathIds);
                    return;
                }
                case KEY_CODES.BACKSPACE:
                case KEY_CODES.DELETE: {
                    event.preventDefault();
                    event.stopPropagation();

                    const selectedIds = Object.keys(selectedPathIdsMap);
                    removePaths(selectedIds);
                    return setSelectedPathIds([]);
                }
                case KEY_CODES.UP_ARROW:
                    if (hasCommandModifier(event) && hasShiftKey(event)) {
                        return handleReorderPathsToTop({
                            event,
                            selectedPathIdsMap,
                            paths,
                            setPaths,
                        });
                    }

                    return handleShiftPaths({
                        event,
                        defaultOffset: { x: 0, y: -1 },
                        selectedPathIdsMap,
                        paths,
                        setPaths,
                        canvasScale,
                    });
                case KEY_CODES.DOWN_ARROW:
                    if (hasCommandModifier(event) && hasShiftKey(event)) {
                        return handleReorderPathsToBottom({
                            event,
                            selectedPathIdsMap,
                            paths,
                            setPaths,
                            canvasScale,
                        });
                    }

                    return handleShiftPaths({
                        event,
                        defaultOffset: { x: 0, y: 1 },
                        selectedPathIdsMap,
                        paths,
                        setPaths,
                        canvasScale,
                    });
                case KEY_CODES.LEFT_ARROW:
                    return handleShiftPaths({
                        event,
                        defaultOffset: { x: -1, y: 0 },
                        selectedPathIdsMap,
                        paths,
                        setPaths,
                        canvasScale,
                    });
                case KEY_CODES.RIGHT_ARROW:
                    return handleShiftPaths({
                        event,
                        defaultOffset: { x: 1, y: 0 },
                        selectedPathIdsMap,
                        paths,
                        setPaths,
                        canvasScale,
                    });
                case KEY_CODES.ESC:
                    return handleSelectModeEscapeKey({
                        event,
                        selectedPathIdsMap,
                        clearSelectedPaths,
                        paths,
                        saveAndExitDrawingEditor,
                        discardAndExitDrawingEditor,
                    });
                default:
                    return null;
            }
        },
        [paths, selectedPathIdsMap],
    );

    useDocumentKeyEventHandler({
        onKeyDown,
    });

    const viewBox = getDrawingViewBox(props);

    return (
        <>
            <DrawingEditorCanvasSvg
                viewBox={viewBox}
                className={classNames('DrawingEditorCanvasSelectMode', { 'drag-mode': isDragging })}
            >
                <DrawingEditorSelectModeSvgStaticGroup
                    paths={paths}
                    canvasScale={canvasScale}
                    selectedPathIdsMap={selectedPathIdsMap}
                    isDragMove={isDragMove}
                    draggedPathIdsMap={draggedPathIdsMap}
                    debugUpdateAllStrokes={debugUpdateAllStrokes}
                    debugDrawingLastUpdateTime={debugDrawingLastUpdateTime}
                />
                <DrawingEditorSelectModeSvgHitAreaGroup
                    paths={paths}
                    debugUpdateAllStrokes={debugUpdateAllStrokes}
                    debugDrawingLastUpdateTime={debugDrawingLastUpdateTime}
                />
                <DrawingEditorSelectModeSvgTransformGroup
                    isDragMove={isDragMove}
                    canvasScale={canvasScale}
                    paths={paths}
                    selectedPathIdsMap={selectedPathIdsMap}
                    draggedPathIdsMap={draggedPathIdsMap}
                    transformGroupRef={transformGroupRef}
                    debugUpdateAllStrokes={debugUpdateAllStrokes}
                    debugDrawingLastUpdateTime={debugDrawingLastUpdateTime}
                />
            </DrawingEditorCanvasSvg>
            {externalInputLayerRef?.current &&
                createPortal(
                    <DrawingSelectionMarquee
                        isMarqueeSelectMode={isMarqueeSelectMode}
                        marqueeDimensions={viewportMarqueeDimensions}
                    />,
                    externalInputLayerRef.current,
                )}
        </>
    );
};

DrawingEditorCanvasSelectMode.propTypes = {
    enableDrawingInkEffect: PropTypes.bool,

    paths: PropTypes.array,
    setPaths: PropTypes.func,
    setToolModeState: PropTypes.func,
    removePaths: PropTypes.func,

    canvasScale: PropTypes.number,
    viewBox: PropTypes.string,
    viewBoxWidth: PropTypes.number,
    viewBoxHeight: PropTypes.number,

    strokeColor: PropTypes.string,
    strokeSize: PropTypes.number,
    setStrokeSizeState: PropTypes.func,
    setStrokeColorState: PropTypes.func,

    closeAllPopups: PropTypes.func,
    saveAndExitDrawingEditor: PropTypes.func,
    discardAndExitDrawingEditor: PropTypes.func,

    debugUpdateAllStrokes: PropTypes.bool,
    debugDrawingLastUpdateTime: PropTypes.number,
    onPathPropertyChangeRef: PropTypes.object,
    externalInputLayerRef: PropTypes.object,
    inputLayerDelegateRef: PropTypes.object,
};

export default DrawingEditorCanvasSelectMode;
